import React, { useState } from 'react';
import { Grid, Box } from '@mui/material';
import { useRecoilState } from 'recoil';
import { memberForm, OrderPayload } from 'pages/myidm/store';
import { AddMemberBtn } from 'pages/myidm/components/index';
import { InputInfo } from 'pages/myidm/components/input-info';
import PropTypes from 'prop-types';
import { useMutation } from 'react-query';
import axios from 'api/request';
import { endPoints } from 'api/endpoints';
import { NON_PRIVILEGED, PRIVILEGED } from 'pages/myidm/utils/constant';
import AdGroupMember from './AdGroupMember';
import ServiceAccountAdValidation from './ServiceAccountAdValidation';
import SecondaryAccountDropdown from './SecondaryAccountDropdown';

const MultipleAddMemberForm = ({ index, localMembers, setLocalMembers }) => {
    const [form, setForm] = useRecoilState(memberForm);
    const [order, setOrder] = useRecoilState(OrderPayload);
    const [, setShowError] = useState({ status: false, message: '' });
    const [secondaryOptions, setSecondaryOptions] = useState([]);

    const isPrivileged = order?.multipleAdGroups?.[index]?.adGroupType === PRIVILEGED;

    const secondaryAccountApi = useMutation((payload) => axios.post(endPoints.myIdm.secondaryAccountDetails, { membersGpid: [payload.gpid] }));

    // Fetch secondary accounts whenever the selected member changes (privileged groups only)
    React.useEffect(() => {
        const gpid = order?.adGrpMemberDetails?.gpid;
        if (isPrivileged && gpid) {
            secondaryAccountApi.mutate(
                { gpid },
                {
                    onSuccess: (data) => {
                        const details = data?.data?.secondaryAccountDetails?.[0]?.accountDetails || [];
                        const mappedOptions = details.map((d) => ({ label: d.secondaryAccountName, ...d }));
                        if (mappedOptions.length === 1) {
                            setSecondaryOptions([]);
                            setOrder((prev) => ({ ...prev, selectedSecondaryAccount: mappedOptions[0], hasSecondaryAccounts: true }));
                        } else if (mappedOptions.length > 1) {
                            setSecondaryOptions(mappedOptions);
                            setOrder((prev) => ({ ...prev, selectedSecondaryAccount: null, hasSecondaryAccounts: true }));
                        } else {
                            setSecondaryOptions([]);
                            setOrder((prev) => ({ ...prev, selectedSecondaryAccount: null, hasSecondaryAccounts: false }));
                        }
                    },
                    onError: () => {
                        setSecondaryOptions([]);
                        setOrder((prev) => ({ ...prev, selectedSecondaryAccount: null, hasSecondaryAccounts: false }));
                    },
                }
            );
        } else {
            setSecondaryOptions([]);
            setOrder((prev) => ({ ...prev, selectedSecondaryAccount: null, hasSecondaryAccounts: false }));
        }
    }, [order?.adGrpMemberDetails?.gpid]);

    const handleAddMember = () => {
        const memberList = localMembers ?? [];
        if (memberList?.length > 9) {
            setShowError({ status: true, message: 'You have reached max limit of 10 members.' });
        } else {
            const member = order?.adGrpMemberDetails
                ? { ...order.adGrpMemberDetails, ...(isPrivileged && { secondaryAccount: order?.selectedSecondaryAccount }) }
                : {
                      gpid: order?.serviceAccount,
                      name: `Service ${order?.serviceAccount}`,
                      email: `${order?.serviceAccount}@pepsico.com`,
                  };
            setLocalMembers([...memberList, member]);
            setSecondaryOptions([]);
            setOrder({
                ...order,
                adGrpMember: null,
                adGrpMemberDetails: null,
                crossDomainUser: null,
                adGrpMemberValidation: { isValid: false, message: null },
                serviceAccount: '',
                serviceAccountValidation: { isValid: false, message: '' },
                selectedSecondaryAccount: null,
                hasSecondaryAccounts: false,
            });
            setForm({
                ...form,
                selectedMember: null,
                member: null,
            });
            setShowError({ status: false, message: '' });
        }
    };

    const isAddMemberInvalid =
        secondaryAccountApi.isLoading ||
        !(
            (order?.adGrpMemberValidation?.isValid &&
                order?.adGrpMemberDetails &&
                (!isPrivileged || !order?.hasSecondaryAccounts || order?.selectedSecondaryAccount)) ||
            (order?.serviceAccountInputFieldValidation?.isValid && order?.serviceAccountValidation?.isValid && order?.serviceAccount)
        );

    return (
        <>
            {' '}
            <Grid item sx={{ mb: 2 }}>
                <Box display="flex" alignItems="flex-start" gap={1}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', flexDirection: 'column', width: '100%', gap: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', flexDirection: 'column', width: '100%', gap: 2 }}>
                            <AdGroupMember index={index} localMembers={localMembers} isSecondaryLoading={secondaryAccountApi.isLoading} />
                            {order?.multipleAdGroups?.[index]?.adGroupType === NON_PRIVILEGED && <ServiceAccountAdValidation localMembers={localMembers} />}
                            {isPrivileged && <SecondaryAccountDropdown options={secondaryOptions} isLoading={secondaryAccountApi.isLoading} />}
                        </Box>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                        <InputInfo title="You can add maximum 10 members." />
                        <AddMemberBtn handleAddMember={() => handleAddMember()} invalid={isAddMemberInvalid} />
                    </Box>
                </Box>
            </Grid>
        </>
    );
};

MultipleAddMemberForm.propTypes = {
    index: PropTypes.number.isRequired,
    localMembers: PropTypes.array,
    setLocalMembers: PropTypes.any.isRequired,
};
MultipleAddMemberForm.defaultProps = {
    localMembers: [],
};

export default React.memo(MultipleAddMemberForm);
